import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import API, { apis } from "common/apis";
import { IApiError } from "common/interface/IApiError";
import { logoutIfNeeded } from "common/utils";
import { IAdminGroups } from "../../common/interface/IAdminGroups";
import { IAdminUsersAPI } from "../../common/interface/IAdminUsers";
import { IPickerMappingUpload } from "common/interface/IPickerUploadScheme";
import { getPersistProcessingProjectedSr, getPersistProcessingSocietySequenceMapping, getPersistProcessingUserDayMapping, getPersistProcessingDynamicClusteringMapping, persistProcessingProjectedSr, persistProcessingSocietySequenceMapping, persistProcessingUserDayMapping, persistProcessingDynamicClusteringMapping, removePersistProcessingProjectedSr, removePersistProcessingSocietySequenceMapping, removePersistProcessingUserDayMapping, removePersistProcessingDynamicClusteringMapping } from "services/PersistService/PersistAdminServices";
import { IRequisitionUserMappingUpload, IStore } from "common/interface";
import { RootState } from "store/store";
import { config } from "process";
import { ClusterInfo, Society, ISocietiesApi } from "common/interface/Society";
import { Amendment, IAmendmentsApi, SubmitAmendmentRequest } from "common/interface/Amendment";
import { getApiErrorDetails } from "common/societyAmendmentUtils";
import { DelayRequest } from "pages/DelayRequetsPage/model/DelayRequest";
import { PpdSlots } from "pages/SocietyServices/PpdSlots";
import { ICityInfo } from "common/interface/ICityInfo";
import { IWhDivision } from "common/interface/IUser";

export interface State {
    formData: Object,
    status: "loading" | "success" | "error" | "idle" | "cancelling" | "saving" | "updating";
    error?: string;
    processingProjectedSrMapping: IPickerMappingUpload;
    processingUserDayMapping: IPickerMappingUpload;
    processingSocietySequenceMapping: IPickerMappingUpload;
    processingDynamicClusteringMapping: IPickerMappingUpload;
    adminUserGroups: IAdminGroups[];
    assignableGroups: IAdminGroups[];
    darkStores: IStore[];
    // rightAwayStores: IStore[];
    cities: ICityInfo[];
    allWhDivisions: IWhDivision[];
    societies: ISocietiesApi;
    ppdSlots: PpdSlots[];
    delayRequests: DelayRequest[];
    clusters: ClusterInfo[];
    adminUsers: IAdminUsersAPI;
    isSocietyActive?: boolean;
    affectedUsersCount?: number;
    amendments: IAmendmentsApi;
    currentAmendment: Amendment | null;
    amendmentStatus: "loading" | "success" | "error" | "idle" | "saving";
    amendmentError?: string;
}

const initialState: State = {
    adminUserGroups: [],
    assignableGroups: [],
    darkStores: [],
    // rightAwayStores: [],
    cities: [],
    allWhDivisions: [],
    clusters: [],
    societies: {
        data: [],
        pageNo: 0,
        pageSize: 0,
        total: 0,
        pages: 0,
    },
    ppdSlots: [],
    delayRequests: [],
    adminUsers: {
        data: [],
        pageNo: 0,
        pageSize: 0,
        total: 0,
        pages: 0,
    },
    formData: {},
    status: "idle",
    processingProjectedSrMapping: getPersistProcessingProjectedSr(),
    processingUserDayMapping: getPersistProcessingUserDayMapping(),
    processingSocietySequenceMapping: getPersistProcessingSocietySequenceMapping(),
    processingDynamicClusteringMapping: getPersistProcessingDynamicClusteringMapping(),
    amendments: {
        data: [],
        pageNo: 0,
        pageSize: 0,
        total: 0,
        pages: 0,
    },
    currentAmendment: null,
    amendmentStatus: "idle",
};

export const saveProcessingProjectedSr = createAsyncThunk<IRequisitionUserMappingUpload>("admin/projected-sr/upload/save", async (_, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    const { adminServices: { processingProjectedSrMapping: { key } } } = state;

    const response = await API.post(apis.saveProjectedSr, {}, {
        params: { key }
    })
        .then(success => success.data)
        .catch(error => {
            const errorMessage = error?.response?.data?.message || "Something went wrong!";
            logoutIfNeeded(error as any, thunkApi);
            return thunkApi.rejectWithValue({ message: errorMessage });
        });

    return response;
});

export const cancelProcessingProjectedSr = createAsyncThunk("admin/projected-sr/upload/cancel", async (_, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    const { adminServices: { processingProjectedSrMapping: { key } } } = state;

    const response = await API.post(apis.saveProjectedSr, {}, { //FixMe: Get Cancel API
        params: { key, cancel: 1 }
    })
        .then(success => success.data)
        .catch(error => {
            const errorMessage = error?.response?.data?.message || "Something went wrong!";
            logoutIfNeeded(error as any, thunkApi);
            return thunkApi.rejectWithValue({ message: errorMessage });
        });

    return response;
});

export const saveProcessingUserDayMapping = createAsyncThunk<IRequisitionUserMappingUpload>("admin/user-day/upload/save", async (_, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    const { adminServices: { processingUserDayMapping: { key } } } = state;

    const response = await API.post(apis.saveUserDayMapping, {}, {
        params: { key }
    })
        .then(success => success.data)
        .catch(error => {
            const errorMessage = error?.response?.data?.message || "Something went wrong!";
            logoutIfNeeded(error as any, thunkApi);
            return thunkApi.rejectWithValue({ message: errorMessage });
        });

    return response;
});

export const cancelProcessingUserDayMapping = createAsyncThunk("admin/user-day/upload/cancel", async (_, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    const { adminServices: { processingUserDayMapping: { key } } } = state;

    const response = await API.post(apis.saveProjectedSr, {}, { //FixMe: Get Cancel API
        params: { key, cancel: 1 }
    })
        .then(success => success.data)
        .catch(error => {
            const errorMessage = error?.response?.data?.message || "Something went wrong!";
            logoutIfNeeded(error as any, thunkApi);
            return thunkApi.rejectWithValue({ message: errorMessage });
        });

    return response;
});

export const saveProcessingSocietySequenceMapping = createAsyncThunk<IRequisitionUserMappingUpload>("admin/society/sequence/upload/save", async (_, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    const { adminServices: { processingSocietySequenceMapping: { key } } } = state;

    const response = await API.post(apis.saveSocietySequenceMapping, {}, {
        params: { key }
    })
        .then(success => success.data)
        .catch(error => {
            const errorMessage = error?.response?.data?.message || "Something went wrong!";
            logoutIfNeeded(error as any, thunkApi);
            return thunkApi.rejectWithValue({ message: errorMessage });
        });

    return response;
});

export const cancelProcessingSocietySequenceMapping = createAsyncThunk("admin/society/sequence/upload/cancel", async (_, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    const { adminServices: { processingSocietySequenceMapping: { key } } } = state;

    const response = await API.post(apis.saveSocietySequenceMapping, {}, {
        params: { key, cancel: 1 }
    })
        .then(success => success.data)
        .catch(error => {
            const errorMessage = error?.response?.data?.message || "Something went wrong!";
            logoutIfNeeded(error as any, thunkApi);
            return thunkApi.rejectWithValue({ message: errorMessage });
        });

    return response;
});

export const saveProcessingDynamicClusteringMapping = createAsyncThunk<IRequisitionUserMappingUpload>("admin/dynamic-clustering/upload/save", async (_, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    const { adminServices: { processingDynamicClusteringMapping: { key } } } = state;

    const response = await API.post(apis.saveDynamicClusteringMapping, {}, {
        params: { key }
    })
        .then(success => success.data)
        .catch(error => {
            const errorMessage = error?.response?.data?.message || "Something went wrong!";
            logoutIfNeeded(error as any, thunkApi);
            return thunkApi.rejectWithValue({ message: errorMessage });
        });

    return response;
});

export const cancelProcessingDynamicClusteringMapping = createAsyncThunk("admin/dynamic-clustering/upload/cancel", async (_, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    const { adminServices: { processingDynamicClusteringMapping: { key } } } = state;

    const response = await API.post(apis.saveDynamicClusteringMapping, {}, {
        params: { key, cancel: 1 }
    })
        .then(success => success.data)
        .catch(error => {
            const errorMessage = error?.response?.data?.message || "Something went wrong!";
            logoutIfNeeded(error as any, thunkApi);
            return thunkApi.rejectWithValue({ message: errorMessage });
        });

    return response;
});

export const addNewUser = createAsyncThunk("/admin-services/add-admin", async (data: any, thunkApi) => {
    data["phone"] = data["phone"];
    data["groups"] = (data.groups ?? []).map((group: number) => {
        return {
            id: group
        }
    });
    data["mappedWh"] = (data.mappedWh ?? []).map((e: number) => ({ id: e }));
    data["mappedDivisions"] = data.mappedDivisions ?? [];

    // designation & shift travel inside the `metadata` blob, not as top-level fields
    const addMeta = { ...(data.metadata ?? {}) };
    if (data.designation != null) {
        addMeta.designation = data.designation;
    }
    if (data.shift != null) {
        addMeta.shift = data.shift;
    }
    if (data.enableLmPerformanceBasedPayout != null) {
        addMeta.enableLmPerformanceBasedPayout = data.enableLmPerformanceBasedPayout;
    }
    data["metadata"] = addMeta;
    delete data.designation;
    delete data.shift;
    delete data.enableLmPerformanceBasedPayout;

    const response = await API.post(apis.addNewAdmin, data)
        .then(success => success.data)
        .catch(error => {
            const errorMessage = error?.response?.data?.message || "Something went wrong!";
            logoutIfNeeded(error as any, thunkApi);
            return thunkApi.rejectWithValue({ message: errorMessage });
        });

    return response;
});

export const addNewSociety = createAsyncThunk("/admin-services/add-society", async (data: any, thunkApi) => {
    const response = await API.post(apis.addNewSociety, data)
        .then(success => success.data)
        .catch(error => {
            const errorMessage = error?.response?.data?.message || "Something went wrong!";
            logoutIfNeeded(error as any, thunkApi);
            return thunkApi.rejectWithValue({ message: errorMessage });
        });

    return response;
});

export const updateSociety = createAsyncThunk("/admin-services/update-society", async (data: any, thunkApi) => {
    let id = data["id"];

    const response = await API.put(apis.addNewSociety + '/' + id, data)
        .then(success => success.data)
        .catch(error => {
            const errorMessage = error?.response?.data?.message || "Something went wrong!";
            logoutIfNeeded(error as any, thunkApi);
            return thunkApi.rejectWithValue({ message: errorMessage });
        });

    return response;
});

export const updateApproveSociety = createAsyncThunk("/admin-services/update-approve-society", async (data: any, thunkApi) => {
    try {
        let id = data["id"];
        const response = await API.put(`${apis.addNewSociety}/request/${id}/approve`, data);
        return response.data;
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || "Something went wrong!";
        logoutIfNeeded(error, thunkApi);
        return thunkApi.rejectWithValue({ message: errorMessage });
    }
});

export const toggleSocietyStatus = createAsyncThunk("admin-service/society-status", async ({id, status}: any, thunkApi) => {
    const response = await API.patch(`${apis.addNewSociety}/${id}/toggle-active?toggle=${status}`)
    .then(success => success.data)
    .catch(error => {
        const errorMessage = error?.response?.data?.message || "Something went wrong!";
        logoutIfNeeded(error, thunkApi);
        return thunkApi.rejectWithValue({ message: errorMessage });
    });
    return response;
});

export const submitSocietyAmendment = createAsyncThunk(
    "/admin-services/submit-society-amendment",
    async (data: SubmitAmendmentRequest, thunkApi) => {
        try {
            const response = await API.post(apis.amendments, data);
            return response.data as Amendment;
        } catch (error) {
            const { message, code } = getApiErrorDetails(error);
            logoutIfNeeded(error as any, thunkApi);
            return thunkApi.rejectWithValue({ message, code });
        }
    }
);

export const getSocietyAmendments = createAsyncThunk<
    IAmendmentsApi,
    {
        pageNo?: number;
        pageSize?: number;
        status?: string;
        requestType?: string;
        entityId?: string;
    }
>("/admin-services/get-society-amendments", async (params, thunkApi) => {
    try {
        const response = await API.get(`${apis.amendments}/paginated`, {
            params: {
                entityType: "SOCIETY",
                pageNo: params.pageNo ?? 1,
                pageSize: params.pageSize ?? 25,
                ...(params.status ? { status: params.status } : {}),
                ...(params.requestType ? { requestType: params.requestType } : {}),
                ...(params.entityId ? { entityId: params.entityId } : {}),
            },
        });
        return response.data as IAmendmentsApi;
    } catch (error) {
        const { message } = getApiErrorDetails(error);
        logoutIfNeeded(error as any, thunkApi);
        return thunkApi.rejectWithValue({ message });
    }
});

export const getAmendmentById = createAsyncThunk<Amendment, number>(
    "/admin-services/get-amendment-by-id",
    async (id, thunkApi) => {
        try {
            const response = await API.get(`${apis.amendments}/${id}`);
            return response.data as Amendment;
        } catch (error) {
            const { message } = getApiErrorDetails(error);
            logoutIfNeeded(error as any, thunkApi);
            return thunkApi.rejectWithValue({ message });
        }
    }
);

export const approveSocietyAmendment = createAsyncThunk(
    "/admin-services/approve-society-amendment",
    async ({ id, remarks }: { id: number; remarks?: string }, thunkApi) => {
        try {
            const response = await API.put(`${apis.amendments}/${id}/approve`, { remarks });
            return response.data as Amendment;
        } catch (error) {
            const { message } = getApiErrorDetails(error);
            logoutIfNeeded(error as any, thunkApi);
            return thunkApi.rejectWithValue({ message });
        }
    }
);

export const rejectSocietyAmendment = createAsyncThunk(
    "/admin-services/reject-society-amendment",
    async ({ id, remarks }: { id: number; remarks: string }, thunkApi) => {
        try {
            const response = await API.put(`${apis.amendments}/${id}/reject`, { remarks });
            return response.data as Amendment;
        } catch (error) {
            const { message, code } = getApiErrorDetails(error);
            logoutIfNeeded(error as any, thunkApi);
            return thunkApi.rejectWithValue({ message, code });
        }
    }
);

export const cancelSocietyAmendment = createAsyncThunk(
    "/admin-services/cancel-society-amendment",
    async ({ id, remarks }: { id: number; remarks?: string }, thunkApi) => {
        try {
            const response = await API.put(`${apis.amendments}/${id}/cancel`, { remarks });
            return response.data as Amendment;
        } catch (error) {
            const { message } = getApiErrorDetails(error);
            logoutIfNeeded(error as any, thunkApi);
            return thunkApi.rejectWithValue({ message });
        }
    }
);

export const updateUser = createAsyncThunk("/admin-services/update-admin", async (data: any, thunkApi) => {
    const id = data["id"];
    data["phone"] = "+91" + data["phone"];
    data["id"] = undefined;
    data["groups"] = (data.groups ?? []).map((group: number) => {
        return {
            id: group
        }
    });
    data["mappedWh"] = (data.mappedWh ?? []).map((e: number) => ({ id: e }));
    data["mappedDivisions"] = data.mappedDivisions ?? [];

    // designation & shift travel inside the `metadata` blob, not as top-level fields.
    // Send null explicitly so the backend clears a removed value.
    const updateMeta = { ...(data.metadata ?? {}) };
    updateMeta.designation = data.designation ?? null;
    updateMeta.shift = data.shift ?? null;
    // Unset is treated as false so clearing the select actually turns the flag off.
    updateMeta.enableLmPerformanceBasedPayout = data.enableLmPerformanceBasedPayout ?? false;
    data["metadata"] = updateMeta;
    delete data.designation;
    delete data.shift;
    delete data.enableLmPerformanceBasedPayout;

    const response = await API.put(apis.getAdminUser + "/" + id, data)
        .then(success => success.data)
        .catch(error => {
            const errorMessage = error?.response?.data?.message || "Something went wrong!";
            logoutIfNeeded(error as any, thunkApi);
            return thunkApi.rejectWithValue({ message: errorMessage });
        });

    return response;
});

export const getAdminGroups = createAsyncThunk("/admin-services/get-admin-groups", async (_, thunkApi) => {
    const response = await API.get(apis.getAdminGroups)
        .then(success => success.data as IAdminGroups[])
        .catch(error => {
            const errorMessage = error?.response?.data?.message || "Something went wrong!";
            logoutIfNeeded(error as any, thunkApi);
            return thunkApi.rejectWithValue({ message: errorMessage });
        });

    return response;
})

export const getAssignableGroups = createAsyncThunk("/admin-services/get-assignable-groups", async (_, thunkApi) => {
    const response = await API.get(apis.getAssignableGroups)
        .then(success => success.data as IAdminGroups[])
        .catch(error => {
            const errorMessage = error?.response?.data?.message || "Something went wrong!";
            logoutIfNeeded(error as any, thunkApi);
            return thunkApi.rejectWithValue({ message: errorMessage });
        });

    return response;
})

export const getDarkStores = createAsyncThunk<IStore[], number | undefined>(
    "/admin-services/get-dark-stores",
    async (cityId, thunkApi) => {
        const params = cityId ? { cityId } : {};
        const response = await API.get(apis.getDarkStore, { params })
            .then(success => success.data as IStore[])
            .catch(error => {
                const errorMessage = error?.response?.data?.message || "Something went wrong!";
                logoutIfNeeded(error as any, thunkApi);
                return thunkApi.rejectWithValue({ message: errorMessage });
            });

        return response;
    }
)

// export const getRightAwayStores = createAsyncThunk("/admin-services/get-right-away-stores", async (_, thunkApi) => {
//     const response = await API.get(apis.getRightAwayStore)
//         .then(success => success.data as IStore[])
//         .catch(error => {
//             const errorMessage = error?.response?.data?.message || "Something went wrong!";
//             logoutIfNeeded(error as any, thunkApi);
//             return thunkApi.rejectWithValue({ message: errorMessage });
//         });

//     return response;
// })

export const getCities = createAsyncThunk("/admin-services/get-cities", async (_, thunkApi) => {
    const response = await API.get(apis.getCitiesList)
        .then(success => success.data as ICityInfo[])
        .catch(error => {
            const errorMessage = error?.response?.data?.message || "Something went wrong!";
            logoutIfNeeded(error as any, thunkApi);
            return thunkApi.rejectWithValue({ message: errorMessage });
        });

    return response;
})

export const getAllWhDivisions = createAsyncThunk("/admin-services/get-all-wh-divisions", async (_, thunkApi) => {
    const response = await API.get(apis.fetchAllWhDivisions)
        .then(success => success.data as IWhDivision[])
        .catch(error => {
            const errorMessage = error?.response?.data?.message || "Something went wrong!";
            logoutIfNeeded(error as any, thunkApi);
            return thunkApi.rejectWithValue({ message: errorMessage });
        });

    return response;
})

export const getClusterListing = createAsyncThunk("/admin-services/get-clusters", async (_, thunkApi) => {
    const response = await API.get(apis.getClusters)
        .then(success => success.data as ClusterInfo[])
        .catch(error => {
            const errorMessage = error?.response?.data?.message || "Something went wrong!";
            logoutIfNeeded(error as any, thunkApi);
            return thunkApi.rejectWithValue({ message: errorMessage });
        });

    return response;
})

export const getSocieties = createAsyncThunk<
    ISocietiesApi,
    { pageNo: number; pageSize: number; filter: string; approved?: number }
>("/admin-services/get-society", async ({ pageNo = 1, pageSize = 25, filter, approved }, thunkApi) => {

    const temp = Object.fromEntries(new URLSearchParams(filter));

    const filterParams = filter
        ? { ...temp }
        : "";

    const params: any = {
        pageNo,
        pageSize,
        ...filterParams,
        isOnlyActive: -1,
    };

    if (approved !== undefined) {
        params.approved = approved;
    }

    const response = await API.get(apis.addNewSociety + "/paginated", {
        params,
    })
        .then(success => success.data as ISocietiesApi)
        .catch(error => {
            const errorMessage = error?.response?.data?.message || "Something went wrong!";
            logoutIfNeeded(error as any, thunkApi);
            return thunkApi.rejectWithValue({ message: errorMessage });
        });

    return response;
})

export const getNearbySocieties = createAsyncThunk<Society[], {childSocietyId: string}>("/admin-services/get-nearby-society", async ({childSocietyId}, thunkApi) => {
    try {
        const response = await API.get(apis.getNearbySocieties, {
            params: {
                childSocietyId: childSocietyId,
            },
        });
        return response.data as Society[];
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || "Something went wrong!";
        logoutIfNeeded(error, thunkApi);
        return thunkApi.rejectWithValue({ message: errorMessage });
    }
})

export const getAffectedUsersCount = createAsyncThunk<{count: number}, {societyId: string}>("/admin-services/get-affected-users-count", async ({societyId}, thunkApi) => {
    try {
        const response = await API.get(`${apis.getAffectedUsersCount}/${societyId}/customer-count`);
        return response.data;
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || "Something went wrong!";
        logoutIfNeeded(error, thunkApi);
        return thunkApi.rejectWithValue({ message: errorMessage });
    }
})

export const mergeSociety = createAsyncThunk<{}, {targetSocietyId: string, sourceSocietyId: string}>("/admin-services/merge-society", async ({targetSocietyId, sourceSocietyId}, thunkApi) => {
    try {
        const response = await API.post(apis.mergeSociety, {
            targetSocietyId: parseInt(targetSocietyId, 10),
            sourceSocietyId: parseInt(sourceSocietyId, 10),
        });
        return response.data;
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || "Something went wrong!";
        logoutIfNeeded(error, thunkApi);
        return thunkApi.rejectWithValue({ message: errorMessage });
    }
})

export const getPpdSlots = createAsyncThunk<PpdSlots[]>("/admin-services/get-ppd-slots", async (_, thunkApi) => {

        const response = await API.get(apis.getPpdSlots, { 
        })
        .then(success => success.data as PpdSlots[])
        .catch(error => {
            const errorMessage = error?.response?.data?.message || "Something went wrong!";
            logoutIfNeeded(error as any, thunkApi);
            return thunkApi.rejectWithValue({ message: errorMessage });
        });

    return response;
})

export const getDelayRequests = createAsyncThunk<DelayRequest[], {filter: string}>("/societies/get-delay-requests", async ({filter}, thunkApi) => {
    
    const temp = Object.fromEntries(new URLSearchParams(filter));

    const filterParams = filter
        ? { ...temp }
        : "";
        console.log("Filters are ", filterParams);
        
        const response = await API.get(apis.getDelayRequests, {
            params: {
                ...filterParams,
                isOnlyActive: -1,
            },
        })
        .then(success => success.data as DelayRequest[])
        .catch(error => {
            const errorMessage = error?.response?.data?.message || "Something went wrong!";
            logoutIfNeeded(error as any, thunkApi);
            return thunkApi.rejectWithValue({ message: errorMessage });
        });

    return response;
})

export const updateDelayStatus = createAsyncThunk("/societies/update-delay-requests", async ({id, status}: {
    id: number;
    status: string;
  }, thunkApi) => {
    const response = await API.put(`${apis.updateDelayRequests}`, {
        "id":id,
        "status":status,
    },)
    .then(success => success.data)
    .catch(error => {
        const errorMessage = error?.response?.data?.message || "Something went wrong!";
        logoutIfNeeded(error, thunkApi);
        return thunkApi.rejectWithValue({ message: errorMessage });
    });
    return response;
});

export const getAdminUsers = createAsyncThunk<
    IAdminUsersAPI,
    { pageNo: number; pageSize: number, filter: string }
>("/admin-services/get-admin-users", async ({ pageNo = 1, pageSize = 25, filter }, thunkApi) => {

    const temp = Object.fromEntries(new URLSearchParams(filter));
    if (temp["phone"]) {
        temp["phone"] = temp['phone'];
    }
    const filterParams = filter
        ? { ...temp }
        : "";
    const response = await API.get(apis.getAdminUsers, {
        params: {
            pageNo,
            pageSize,
            ...filterParams,
        },
    })
        .then((success) => {
            const payload = success.data;
            if (!payload?.data) {
                return payload;
            }
            // Surface designation & shift from the `metadata` blob as flat fields.
            // Spread first so we don't mutate the Axios response object.
            return {
                ...payload,
                data: payload.data.map((user: any) => ({
                    ...user,
                    designation: user?.metadata?.designation ?? null,
                    shift: user?.metadata?.shift ?? null,
                    enableLmPerformanceBasedPayout:
                        user?.metadata?.enableLmPerformanceBasedPayout ?? null,
                })),
            };
        })
        .catch((error) => {
            const errorMessage =
                error?.response?.data?.message || "Something went wrong!";
            logoutIfNeeded(error as any, thunkApi);
            return thunkApi.rejectWithValue({ message: errorMessage });
        });

    return response;
});

export const getAdminUserDetail = createAsyncThunk("/admin-services/get-admin-user-detail", async (id: string, thunkApi) => {
    const response = await API.get(apis.getAdminUser + "/" + id)
        .then((success) => {
            if (!success.data) {
                return success.data;
            }
            // Surface designation & shift from the `metadata` blob as flat fields.
            // Spread first so we don't mutate the Axios response object.
            const user = { ...success.data };
            user.designation = user?.metadata?.designation ?? null;
            user.shift = user?.metadata?.shift ?? null;
            user.enableLmPerformanceBasedPayout =
                user?.metadata?.enableLmPerformanceBasedPayout ?? null;
            return user;
        })
        .catch((error) => {
            const errorMessage =
                error?.response?.data?.message || "Something went wrong!";
            logoutIfNeeded(error as any, thunkApi);
            return thunkApi.rejectWithValue({ message: errorMessage });
        });

    return response;
});

const adminServicesSlice = createSlice({
    name: "admin-services",
    initialState,
    reducers: {
        setFormData: (state, action) => {
            state.status = "idle";
            state.error = "";
            state.formData = action.payload;
        },
        resetForm: (state) => {
            state.formData = {};
            state.status = "idle";
            state.error = "";
        },
        setProcessingProjectedSr: (state, action) => {
            state.status = "idle";
            state.error = "";
            state.processingProjectedSrMapping = action.payload;
            state.processingProjectedSrMapping.timeUploaded = new Date().toString();
            persistProcessingProjectedSr(state.processingProjectedSrMapping);
        },
        removeProcessingProjectedSr: (state) => {
            state.processingProjectedSrMapping = {};
            state.status = "idle";
            state.error = "";
            removePersistProcessingProjectedSr();
        },
        setProcessingUserDay: (state, action) => {
            state.status = "idle";
            state.error = "";
            state.processingUserDayMapping = action.payload;
            state.processingUserDayMapping.timeUploaded = new Date().toString();
            persistProcessingUserDayMapping(state.processingProjectedSrMapping);
        },
        removeProcessingUserDay: (state) => {
            state.processingUserDayMapping = {};
            state.status = "idle";
            state.error = "";
            removePersistProcessingUserDayMapping();
        },
        setProcessingSocietySequenceDay: (state, action) => {
            state.status = "idle";
            state.error = "";
            state.processingSocietySequenceMapping = action.payload;
            state.processingUserDayMapping.timeUploaded = new Date().toString();
            persistProcessingSocietySequenceMapping(state.processingProjectedSrMapping);
        },
        removeProcessingSocietySequenceDay: (state) => {
            state.processingSocietySequenceMapping = {};
            state.status = "idle";
            state.error = "";
            removePersistProcessingSocietySequenceMapping();
        },
        setProcessingDynamicClusteringDay: (state, action) => {
            state.status = "idle";
            state.error = "";
            state.processingDynamicClusteringMapping = action.payload;
            state.processingDynamicClusteringMapping.timeUploaded = new Date().toString();
            persistProcessingDynamicClusteringMapping(state.processingDynamicClusteringMapping);
        },
        removeProcessingDynamicClusteringDay: (state) => {
            state.processingDynamicClusteringMapping = {};
            state.status = "idle";
            state.error = "";
            removePersistProcessingDynamicClusteringMapping();
        },
    },
    extraReducers(builder) {
        builder

            /**
            * CREATE NEW ADMIN
            **/
            .addCase(addNewUser.pending, state => {
                state.status = "saving";
            })
            .addCase(addNewUser.fulfilled, state => {
                state.status = "success";
                state.error = "";
                state.formData = {};
            })
            .addCase(addNewUser.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.status = "error";

                if (error.message) {
                    state.error = error.message;
                }
            })
            
            /**
            * CREATE NEW SOCIETY
            **/
            .addCase(addNewSociety.pending, state => {
                state.status = "saving";
            })
            .addCase(addNewSociety.fulfilled, state => {
                state.status = "success";
                state.error = "";
                state.formData = {};
            })
            .addCase(addNewSociety.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.status = "error";
                if (error.message) {
                    state.error = error.message;
                }
            })
            
            
            /**
            * UPDATE SOCIETY
            **/
            .addCase(updateSociety.pending, state => {
                state.status = "saving";
            })
            .addCase(updateSociety.fulfilled, state => {
                state.status = "success";
                state.error = "";
                state.formData = {};
            })
            .addCase(updateSociety.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.status = "error";
                if (error.message) {
                    state.error = error.message;
                }
            })

            /**
            * UPDATE APPROVE SOCIETY
            **/
            .addCase(updateApproveSociety.pending, state => {
                state.status = "saving";
            })
            .addCase(updateApproveSociety.fulfilled, state => {
                state.status = "success";
                state.error = "";
                state.formData = {};
            })
            .addCase(updateApproveSociety.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.status = "error";
                if (error.message) {
                    state.error = error.message;
                }
            })

            /*
            * TOGGLE SOCIETY STATUS
            */
            .addCase(toggleSocietyStatus.pending, state => {
                state.status = "saving";
            })
            .addCase(toggleSocietyStatus.fulfilled, (state, action) => {
                state.status = "idle";
                state.error = "";
                let targetIndex = state.societies.data.findIndex(society => society.id === action.payload.id);
                if (targetIndex !== -1) {
                    state.societies.data[targetIndex].active = action.payload["active"];
                }
            })
            .addCase(toggleSocietyStatus.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.status = "error";

                if (error.message) {
                    state.error = error.message;
                }
            })
            
            /*
            * TOGGLE DELAY REQUEST
            */
            .addCase(updateDelayStatus.pending, state => {                
                state.status = "updating";
            })
            .addCase(updateDelayStatus.fulfilled, (state, action) => {
                state.status = "success";
                state.error = "";
                let targetIndex = state.delayRequests.findIndex(req => req.id === action.meta.arg.id);
                if (targetIndex !== -1) {
                    state.delayRequests[targetIndex].status = action.meta.arg.status;
                }
            })
            .addCase(updateDelayStatus.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.status = "error";

                if (error.message) {
                    state.error = error.message;
                }
            })

            /**
             * GET SOCIETIES
             **/
            .addCase(getSocieties.pending, state => {
                state.status = "loading";
            })
            .addCase(getSocieties.fulfilled, (state, action) => {
                state.status = "idle";
                state.error = "";
                state.societies = action.payload;
            })
            .addCase(getSocieties.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.status = "error";

                if (error.message) {
                    state.error = error.message;
                }
            })
            
            /**
             * GET PPD SLOTS
             **/
            .addCase(getPpdSlots.pending, state => {
                state.status = "loading";
            })
            .addCase(getPpdSlots.fulfilled, (state, action) => {
                state.status = "idle";
                state.error = "";
                state.ppdSlots = action.payload;
            })
            .addCase(getPpdSlots.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.status = "error";

                if (error.message) {
                    state.error = error.message;
                }
            })
            
            /**
             * GET AFFECTED USERS COUNT
             **/
            .addCase(getAffectedUsersCount.pending, state => {
                state.status = "loading";
            })
            .addCase(getAffectedUsersCount.fulfilled, (state, action) => {
                state.status = "idle";
                state.error = "";
                state.affectedUsersCount = action.payload.count;
            })
            .addCase(getAffectedUsersCount.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.status = "error";

                if (error.message) {
                    state.error = error.message;
                }
            })

            /**
             * MERGE SOCIETY
             **/
            .addCase(mergeSociety.pending, state => {
                state.status = "saving";
            })
            .addCase(mergeSociety.fulfilled, (state) => {
                state.status = "success";
                state.error = "";
            })
            .addCase(mergeSociety.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.status = "error";

                if (error.message) {
                    state.error = error.message;
                }
            })

            /**
             * GET DELAY REQUESTS
             **/
            .addCase(getDelayRequests.pending, state => {
                state.status = "loading";
            })
            .addCase(getDelayRequests.fulfilled, (state, action) => {
                state.status = "idle";
                state.error = "";
                state.delayRequests = action.payload;
            })
            .addCase(getDelayRequests.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.status = "error";

                if (error.message) {
                    state.error = error.message;
                }
            })
            
            /**
             * GET CLUSTERS
             **/
            .addCase(getClusterListing.pending, state => {
                state.status = "loading";
            })
            .addCase(getClusterListing.fulfilled, (state, action) => {
                state.status = "idle";
                state.error = "";
                state.clusters = action.payload;
            })
            .addCase(getClusterListing.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.status = "error";

                if (error.message) {
                    state.error = error.message;
                }
            })

            /**
             * GET ADMIN USERS
             **/
            .addCase(getAdminUsers.pending, state => {
                state.status = "loading";
            })
            .addCase(getAdminUsers.fulfilled, (state, action) => {
                state.status = "idle";
                state.error = "";
                state.adminUsers = action.payload;
            })
            .addCase(getAdminUsers.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.status = "error";

                if (error.message) {
                    state.error = error.message;
                }
            })
            /**
             * GET ADMIN USER DETAIL
             **/
            .addCase(getAdminUserDetail.pending, state => {
                state.status = "loading";
            })
            .addCase(getAdminUserDetail.fulfilled, (state, action) => {
                state.status = "idle";
                state.error = "";
            })
            .addCase(getAdminUserDetail.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.status = "error";

                if (error.message) {
                    state.error = error.message;
                }
            })

            /**
             * UPDATE ADMIN USER DETAIL
             **/
            .addCase(updateUser.pending, state => {
                state.status = "saving";
            })
            .addCase(updateUser.fulfilled, (state, action) => {
                state.status = "success";
                state.error = "";
            })
            .addCase(updateUser.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.status = "error";

                if (error.message) {
                    state.error = error.message;
                }
            })

            /**
             * GET ADMIN GROUPS
             **/
            .addCase(getAdminGroups.pending, state => {
                state.status = "loading";
            })
            .addCase(getAdminGroups.fulfilled, (state, action) => {
                state.status = "idle";
                state.error = "";
                state.adminUserGroups = action.payload;
            })
            .addCase(getAdminGroups.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.status = "error";

                if (error.message) {
                    state.error = error.message;
                }
            })
            /**
             * GET ASSIGNABLE GROUPS
             **/
            .addCase(getAssignableGroups.pending, state => {
                state.status = "loading";
            })
            .addCase(getAssignableGroups.fulfilled, (state, action) => {
                state.status = "idle";
                state.error = "";
                state.assignableGroups = action.payload;
            })
            .addCase(getAssignableGroups.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.status = "error";

                if (error.message) {
                    state.error = error.message;
                }
            })
            /**
             * GET DARK STORES
            **/
            .addCase(getDarkStores.pending, state => {
                state.status = "loading";
            })
            .addCase(getDarkStores.fulfilled, (state, action) => {
                state.status = "idle";
                state.error = "";
                state.darkStores = action.payload;
            })
            .addCase(getDarkStores.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.status = "error";
                if (error.message) {
                    state.error = error.message;
                }
            })
            /**
             * GET RIGHT AWAY STORES
            **/
            // .addCase(getRightAwayStores.pending, state => {
            //     state.status = "loading";
            // })
            // .addCase(getRightAwayStores.fulfilled, (state, action) => {
            //     state.status = "idle";
            //     state.error = "";
            //     state.rightAwayStores = action.payload;
            // })
            // .addCase(getRightAwayStores.rejected, (state, action) => {
            //     const error = action.payload as IApiError;
            //     state.status = "error";
            //     if (error.message) {
            //         state.error = error.message;
            //     }
            // })
            /**
             * GET CITIES
            **/
            .addCase(getCities.pending, state => {
                state.status = "loading";
            })
            .addCase(getCities.fulfilled, (state, action) => {
                state.status = "idle";
                state.error = "";
                state.cities = action.payload;
            })
            .addCase(getCities.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.status = "error";
                if (error.message) {
                    state.error = error.message;
                }
            })
            .addCase(getAllWhDivisions.fulfilled, (state, action) => {
                state.allWhDivisions = action.payload as IWhDivision[];
            })
            /**
            * SAVE PROJECTED SR
            **/
            .addCase(saveProcessingProjectedSr.pending, state => {
                state.status = "loading";
            })
            .addCase(saveProcessingProjectedSr.fulfilled, state => {
                state.status = "success";
                state.error = "";
                state.processingProjectedSrMapping = {};
                removeProcessingProjectedSr();
            })
            .addCase(saveProcessingProjectedSr.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.status = "error";

                if (error.message) {
                    state.error = error.message;
                }
            })

            /**
             * CANCEL PROJECTED SR
            **/
            .addCase(cancelProcessingProjectedSr.pending, state => {
                state.status = "cancelling";
            })
            .addCase(cancelProcessingProjectedSr.fulfilled, state => {
                state.status = "idle";
                state.error = "";
                state.processingProjectedSrMapping = {};
                removeProcessingProjectedSr();
            })
            .addCase(cancelProcessingProjectedSr.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.status = "error";
                if (error.message) {
                    state.error = error.message;
                }
            })

            /**
            * SAVE USER DAY MAPPING
            **/
            .addCase(saveProcessingUserDayMapping.pending, state => {
                state.status = "loading";
            })
            .addCase(saveProcessingUserDayMapping.fulfilled, state => {
                state.status = "success";
                state.error = "";
                state.processingUserDayMapping = {};
                removePersistProcessingUserDayMapping();
            })
            .addCase(saveProcessingUserDayMapping.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.status = "error";

                if (error.message) {
                    state.error = error.message;
                }
            })

            /**
             * CANCEL USER DAY MAPPING
            **/
            .addCase(cancelProcessingUserDayMapping.pending, state => {
                state.status = "cancelling";
            })
            .addCase(cancelProcessingUserDayMapping.fulfilled, state => {
                state.status = "idle";
                state.error = "";
                state.processingUserDayMapping = {};
                removePersistProcessingUserDayMapping();
            })
            .addCase(cancelProcessingUserDayMapping.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.status = "error";
                if (error.message) {
                    state.error = error.message;
                }
            })


            /**
            * SAVE SOCIETY SEQUENCE MAPPING
            **/
            .addCase(saveProcessingSocietySequenceMapping.pending, state => {
                state.status = "loading";
            })
            .addCase(saveProcessingSocietySequenceMapping.fulfilled, state => {
                state.status = "success";
                state.error = "";
                state.processingSocietySequenceMapping = {};
                removeProcessingSocietySequenceDay();
            })
            .addCase(saveProcessingSocietySequenceMapping.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.status = "error";

                if (error.message) {
                    state.error = error.message;
                }
            })

            /**
             * CANCEL SOCIETY SEQUENCE MAPPING
            **/
            .addCase(cancelProcessingSocietySequenceMapping.pending, state => {
                state.status = "cancelling";
            })
            .addCase(cancelProcessingSocietySequenceMapping.fulfilled, state => {
                state.status = "idle";
                state.error = "";
                state.processingSocietySequenceMapping = {};
                removeProcessingSocietySequenceDay();
            })
            .addCase(cancelProcessingSocietySequenceMapping.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.status = "error";
                if (error.message) {
                    state.error = error.message;
                }
            })

            /**
            * SAVE DYNAMIC CLUSTERING MAPPING
            **/
            .addCase(saveProcessingDynamicClusteringMapping.pending, state => {
                state.status = "loading";
            })
            .addCase(saveProcessingDynamicClusteringMapping.fulfilled, state => {
                state.status = "success";
                state.error = "";
                state.processingDynamicClusteringMapping = {};
                removePersistProcessingDynamicClusteringMapping();
            })
            .addCase(saveProcessingDynamicClusteringMapping.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.status = "error";

                if (error.message) {
                    state.error = error.message;
                }
            })

            /**
             * CANCEL DYNAMIC CLUSTERING MAPPING
            **/
            .addCase(cancelProcessingDynamicClusteringMapping.pending, state => {
                state.status = "cancelling";
            })
            .addCase(cancelProcessingDynamicClusteringMapping.fulfilled, state => {
                state.status = "idle";
                state.error = "";
                state.processingDynamicClusteringMapping = {};
                removePersistProcessingDynamicClusteringMapping();
            })
            .addCase(cancelProcessingDynamicClusteringMapping.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.status = "error";
                if (error.message) {
                    state.error = error.message;
                }
            })

            .addCase(submitSocietyAmendment.pending, (state) => {
                state.amendmentStatus = "saving";
            })
            .addCase(submitSocietyAmendment.fulfilled, (state) => {
                state.amendmentStatus = "success";
                state.amendmentError = "";
            })
            .addCase(submitSocietyAmendment.rejected, (state, action) => {
                const error = action.payload as IApiError & { code?: string };
                state.amendmentStatus = "error";
                state.amendmentError = error?.message;
            })

            .addCase(getSocietyAmendments.pending, (state) => {
                state.amendmentStatus = "loading";
            })
            .addCase(getSocietyAmendments.fulfilled, (state, action) => {
                state.amendmentStatus = "idle";
                state.amendments = action.payload;
                state.amendmentError = "";
            })
            .addCase(getSocietyAmendments.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.amendmentStatus = "error";
                state.amendmentError = error?.message;
            })

            .addCase(getAmendmentById.pending, (state) => {
                state.amendmentStatus = "loading";
            })
            .addCase(getAmendmentById.fulfilled, (state, action) => {
                state.amendmentStatus = "idle";
                state.currentAmendment = action.payload;
                state.amendmentError = "";
            })
            .addCase(getAmendmentById.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.amendmentStatus = "error";
                state.amendmentError = error?.message;
                state.currentAmendment = null;
            })

            .addCase(approveSocietyAmendment.pending, (state) => {
                state.amendmentStatus = "saving";
            })
            .addCase(approveSocietyAmendment.fulfilled, (state, action) => {
                state.amendmentStatus = "success";
                state.currentAmendment = action.payload;
                state.amendmentError = "";
            })
            .addCase(approveSocietyAmendment.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.amendmentStatus = "error";
                state.amendmentError = error?.message;
            })

            .addCase(rejectSocietyAmendment.pending, (state) => {
                state.amendmentStatus = "saving";
            })
            .addCase(rejectSocietyAmendment.fulfilled, (state, action) => {
                state.amendmentStatus = "success";
                state.currentAmendment = action.payload;
                state.amendmentError = "";
            })
            .addCase(rejectSocietyAmendment.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.amendmentStatus = "error";
                state.amendmentError = error?.message;
            })

            .addCase(cancelSocietyAmendment.pending, (state) => {
                state.amendmentStatus = "saving";
            })
            .addCase(cancelSocietyAmendment.fulfilled, (state, action) => {
                state.amendmentStatus = "success";
                state.currentAmendment = action.payload;
                state.amendmentError = "";
            })
            .addCase(cancelSocietyAmendment.rejected, (state, action) => {
                const error = action.payload as IApiError;
                state.amendmentStatus = "error";
                state.amendmentError = error?.message;
            })


    }
});

export const {
    setFormData,
    setProcessingProjectedSr,
    removeProcessingProjectedSr,
    setProcessingUserDay,
    removeProcessingUserDay,
    setProcessingSocietySequenceDay,
    removeProcessingSocietySequenceDay,
    setProcessingDynamicClusteringDay,
    removeProcessingDynamicClusteringDay,
    resetForm
} = adminServicesSlice.actions;
export default adminServicesSlice.reducer; 